import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import AdminView from './AdminView';
import AdminLogin from './AdminLogin';
import StorefrontView from './StorefrontView';
import './App.css';

export default function App() {
  const [tab, setTab] = useState('store');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminUnlocked, setAdminUnlocked] = useState(
    () => localStorage.getItem('admin-unlocked') === 'true'
  );

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('items')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error loading items:', error.message);
    } else {
      setItems(data);
    }
    setLoading(false);
  };

  const publishItem = async (item) => {
    let imageUrl = null;
    if (item.imageFile) {
      const ext = item.imageFile.name.split('.').pop();
      const filename = `${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('item-photos')
        .upload(filename, item.imageFile);
      if (uploadError) {
        console.error('Photo upload error:', uploadError.message);
      } else {
        const { data: urlData } = supabase.storage
          .from('item-photos')
          .getPublicUrl(filename);
        imageUrl = urlData.publicUrl;
      }
    }

    const { data, error } = await supabase
      .from('items')
      .insert({
        title: item.title,
        description: item.description,
        tags: item.tags,
        category: item.category,
        price: item.price,
        estimated_low: item.estimatedLow,
        estimated_high: item.estimatedHigh,
        status: 'available',
        image_url: imageUrl,
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving item:', error.message);
    } else {
      setItems(prev => [data, ...prev]);
    }
  };

  const removeItem = async (id) => {
    const item = items.find(i => i.id === id);
    if (item?.image_url) {
      const filename = item.image_url.split('/').pop();
      await supabase.storage.from('item-photos').remove([filename]);
    }
    const { error } = await supabase.from('items').delete().eq('id', id);
    if (error) {
      console.error('Error removing item:', error.message);
    } else {
      setItems(prev => prev.filter(i => i.id !== id));
    }
  };

  const markPending = async (id) => {
    const { error } = await supabase
      .from('items')
      .update({ status: 'pending' })
      .eq('id', id);
    if (error) {
      console.error('Error updating item:', error.message);
    } else {
      setItems(prev => prev.map(i =>
        i.id === id ? { ...i, status: 'pending' } : i
      ));
    }
  };

  const lock = () => {
    localStorage.removeItem('admin-unlocked');
    setAdminUnlocked(false);
    setTab('store');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '15px', color: '#888' }}>
        Loading…
      </div>
    );
  }

  return (
    <div className="app">
      <nav className="app-nav">
        <div className="nav-inner">
          <span className="nav-logo">🏷️ Paseo Yard Sale</span>
          <div className="nav-tabs">
            <button
              className={`nav-tab ${tab === 'store' ? 'active' : ''}`}
              onClick={() => setTab('store')}
            >
              Storefront
            </button>
            <button
              className={`nav-tab ${tab === 'admin' ? 'active' : ''}`}
              onClick={() => setTab('admin')}
            >
              Admin
            </button>
          </div>
          {adminUnlocked && (
            <button className="lock-btn" onClick={lock} title="Lock admin panel">
              🔒 Lock
            </button>
          )}
        </div>
      </nav>

      {tab === 'store' && (
        <StorefrontView items={items} onMarkPending={markPending} />
      )}
      {tab === 'admin' && !adminUnlocked && (
        <AdminLogin onUnlock={() => setAdminUnlocked(true)} />
      )}
      {tab === 'admin' && adminUnlocked && (
        <AdminView items={items} onPublish={publishItem} onRemove={removeItem} />
      )}
    </div>
  );
}
